import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableAPIData, LastElementTableColumns } from "components/shared/LastElementTable/type";
import ModalBase from "components/shared/ModalBase";
import type { IStatusRule } from "components/shared/StatusChip";
import StatusChip from "components/shared/StatusChip";
import TextButton from "components/shared/TextButton";
import type { WhiteDropdownMenuOptions } from "components/shared/WhiteDropdownMenu";
import { WhiteDropdownMenu } from "components/shared/WhiteDropdownMenu";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IPayment } from "store/paymentSlice";
import { downloadLotAccountingFile, downloadLotBankFile } from "store/paymentSlice";
import { useAppDispatch, useAppSelector } from "store/store";

import variables from "../../../assets/styles/_variables.module.scss";
import { EPaymentTableStatus } from "..";
import PaymentUpdateModal from "../PaymentUpdateModal";
import styles from "./style.module.scss";

interface IPaymentLotsTable {
    allPayments: IPayment[];
    paymentStatusRules: Record<number, IStatusRule>;
    APIData: LastElementTableAPIData;
    handlePaymentFilter: (lotId: number | undefined) => void;
    onAccountingExported: () => void;
}

interface IPaymentLot {
    id: number;
    paymentsCount: number;
    netTotal: number;
    paymentDate: Date | string;
    status: EPaymentTableStatus.TO_PAY | EPaymentTableStatus.PAID;
}

/**
 * Table grouping payments by lotId.
 */
const PaymentLotsTable = ({
    paymentStatusRules,
    APIData,
    handlePaymentFilter,
    onAccountingExported,
}: IPaymentLotsTable) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const [modalType, setModalType] = useState("");
    const [currentLot, setCurrentLot] = useState<IPaymentLot | null>(null);
    const [updateType, setUpdateType] = useState<"date" | "erase">("date");

    const { paymentLots, paymentLotsStatus } = useAppSelector((state) => state.payment);

    const handleLotAccountingExport = () => {
        if (!currentLot) return;
        dispatch(downloadLotAccountingFile({ paymentLotId: currentLot.id })).then((res) => {
            if (res.meta.requestStatus === "fulfilled") onAccountingExported();
        });
    };

    const handleLotBankFile = () => {
        if (!currentLot) return;
        dispatch(downloadLotBankFile({ paymentLotId: currentLot.id })).then((res) => {
            if (res.meta.requestStatus === "fulfilled") onAccountingExported();
        });
    };

    const menuOptions: WhiteDropdownMenuOptions[] = [
        {
            id: 1,
            name: t("PartialPayment.LotsTable.Edit"),
            action: () => {
                setModalType("update");
                setUpdateType("date");
            },
        },
        {
            id: 2,
            name: t("PartialPayment.LotsTable.ExportAccounting"),
            action: () => handleLotAccountingExport(),
            trackedEvent: "payments-accounting-file'",
        },
        {
            id: 3,
            name: t("PartialPayment.LotsTable.ExportBank"),
            action: () => handleLotBankFile(),
        },
        {
            id: 4,
            name: t("PartialPayment.Delete"),
            action: () => {
                setModalType("update");
                setUpdateType("erase");
            },
            trackedEvent: "payments-delete",
        },
    ];

    const columns: LastElementTableColumns[] = [
        {
            id: "id",
            label: t("PartialPayment.LotsTable.PaymentLot"),
            align: "left",
            format: (id: number, _, lot: IPaymentLot) => (
                <div className={styles["id-column"]}>
                    <span onClick={() => handlePaymentFilter(lot.id)}>#{id}</span>
                    <span>{`${lot.paymentsCount} paiement(s) associé(s)`}</span>
                </div>
            ),
        },
        {
            id: "beneficiariesName",
            label: t("PartialPayment.PaymentModal.Beneficiaries"),
            align: "left",
            format: (names: string, __, lot: IPaymentLot) => (
                <div className={styles["beneficiaries-column"]}>
                    {lot.paymentsCount > 3 ? names + t("PartialPayment.PaymentModal.AndMore") : names}
                </div>
            ),
        },
        {
            id: "netTotal",
            label: t("Payment.Net"),
            align: "right",
            format: (amount: number) => (
                <span>{formatNumber(amount / 100, { isCurrency: true, always2Digit: true })}</span>
            ),
            sort: true,
        },
        {
            id: "status",
            label: t("Payment.PaymentStatus"),
            align: "left",
            format: (status: number, __, lot: IPaymentLot) => {
                return (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            justifyContent: "space-between",
                            maxWidth: "176px",
                        }}
                        key={`status-${lot?.id}`}
                    >
                        <StatusChip
                            statusRules={paymentStatusRules}
                            status={status}
                            extraText={
                                status === EPaymentTableStatus.CANCELLED
                                    ? `le ${formatLocaleDateString({
                                          date: lot.paymentDate,
                                          options: { numeric: true, timeZone: "UTC" },
                                      })}`
                                    : ""
                            }
                        />
                    </div>
                );
            },
            sort: false,
        },
        {
            id: "paymentDate",
            label: t("PartialPayment.LotsTable.PaymentDate"),
            align: "right",
            format: (date: string, _, lot: IPaymentLot) => {
                return (
                    <span>
                        {lot.status === EPaymentTableStatus.PAID
                            ? formatLocaleDateString({ date, options: { numeric: true, timeZone: "UTC" } })
                            : "-"}
                    </span>
                );
            },
        },
        {
            id: "action",
            label: "",
            align: "right",
            format: (_, __, lot) => (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <WhiteDropdownMenu
                        options={menuOptions}
                        buttonName={t("PartialPayment.LotsTable.SeePayments")}
                        onMenuClick={() => setCurrentLot(lot)}
                        mainAction={() => handlePaymentFilter(lot.id)}
                    />
                </div>
            ),
            sort: false,
        },
    ];

    const lotsPreviouslyFetched = APIData.requestStatus === "idle" && !["idle", "pending"].includes(paymentLotsStatus);
    const isLoading = lotsPreviouslyFetched ? false : ["idle", "pending"].includes(APIData.requestStatus ?? "");

    return (
        <Fragment>
            <ModalBase
                open={modalType === "update"}
                onClose={() => setModalType("")}
                modalTitle={t("PartialPayment.LotsTable.ModalTitle")}
                modalSubtitle={t("PartialPayment.LotsTable.ModalSubtitle")}
            >
                <PaymentUpdateModal
                    type="lot"
                    paymentLotId={currentLot?.id}
                    handleClose={() => setModalType("")}
                    updateType={updateType}
                    context="payments-batch-correct"
                />
            </ModalBase>
            <LastElementTable
                data={paymentLots}
                columns={columns}
                max={-1}
                dataType="payment"
                stickyFooter
                APIData={APIData}
                dataCount={APIData.count}
                isLoading={isLoading}
                columnsNumber={6}
            />
        </Fragment>
    );
};

export default PaymentLotsTable;
